import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import AdminLayout from "@/components/layouts/AdminLayout";
import {
  Card, CardContent, CardHeader, CardTitle,
} from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CheckCircle, AlertCircle, Clock, Eye, Search, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const AdminKYC = () => {
  const { getAuthHeaders } = useAuth();
  const [role, setRole] = useState("user");
  const [submissions, setSubmissions] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewStatus, setReviewStatus] = useState("approved");
  const [reviewNote, setReviewNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("role", role);
      params.set("limit", "100");
      if (statusFilter && statusFilter !== "all") params.set("status", statusFilter);
      const res = await axios.get(`${API}/admin/kyc?${params.toString()}`, {
        headers: getAuthHeaders(),
      });
      setSubmissions(res.data.submissions || []);
    } catch (err) {
      toast.error("Failed to load KYC submissions");
    }
    setLoading(false);
  }, [role, statusFilter, getAuthHeaders]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const filtered = submissions.filter((s) => {
    if (!search) return true;
    const kd = s.kyc_data || {};
    const hay = [
      s.name, s.mobile, s.email, s.business_name,
      kd.full_name, kd.pan_number, kd.aadhaar_number,
    ].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(search.toLowerCase());
  });

  const statusBadge = (st) => {
    const cfg = {
      approved: { cls: "bg-green-100 text-green-700", icon: <CheckCircle className="w-3 h-3 mr-1" /> },
      rejected: { cls: "bg-red-100 text-red-700", icon: <AlertCircle className="w-3 h-3 mr-1" /> },
      under_review: { cls: "bg-blue-100 text-blue-700", icon: <Clock className="w-3 h-3 mr-1" /> },
      resubmit: { cls: "bg-amber-100 text-amber-700", icon: <AlertCircle className="w-3 h-3 mr-1" /> },
      pending: { cls: "bg-yellow-100 text-yellow-700", icon: <Clock className="w-3 h-3 mr-1" /> },
    };
    const c = cfg[st] || cfg.pending;
    return <Badge className={c.cls}>{c.icon}{st}</Badge>;
  };

  const openReview = (submission) => {
    setSelected(submission);
    setReviewStatus(submission.kyc_status || "approved");
    setReviewNote(submission.kyc_admin_note || "");
    setReviewDialogOpen(true);
  };

  const saveReview = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      const path = role === "seller"
        ? `${API}/admin/sellers/${selected.id}/kyc`
        : `${API}/admin/users/${selected.id}/kyc`;
      const params = new URLSearchParams();
      params.set("status", reviewStatus);
      if (reviewNote) params.set("admin_note", reviewNote);
      await axios.put(`${path}?${params.toString()}`, {}, { headers: getAuthHeaders() });
      toast.success("KYC status updated");
      setReviewDialogOpen(false);
      fetchSubmissions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
    setSubmitting(false);
  };

  return (
    <AdminLayout title="KYC Review">
      <Card className="border-0 shadow-sm mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-primary-600" /> KYC Submissions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={role} onValueChange={setRole} className="mb-4">
            <TabsList>
              <TabsTrigger value="user" data-testid="kyc-tab-users">Users</TabsTrigger>
              <TabsTrigger value="seller" data-testid="kyc-tab-sellers">Sellers</TabsTrigger>
            </TabsList>
            <TabsContent value={role} />
          </Tabs>

          <div className="flex flex-col md:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by name, mobile, PAN, Aadhaar..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="kyc-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-56" data-testid="kyc-status-filter">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="under_review">Under Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="resubmit">Resubmit</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="text-center py-10 text-gray-500">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10 text-gray-500">No submissions found</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name / Business</TableHead>
                  <TableHead>Mobile</TableHead>
                  <TableHead>PAN</TableHead>
                  <TableHead>Aadhaar</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((sub) => {
                  const kd = sub.kyc_data || {};
                  return (
                    <TableRow key={sub.id}>
                      <TableCell className="font-medium">
                        {sub.name || sub.business_name || kd.full_name || "—"}
                      </TableCell>
                      <TableCell>{sub.mobile}</TableCell>
                      <TableCell>{kd.pan_number || "—"}</TableCell>
                      <TableCell>
                        {kd.aadhaar_number ? "XXXX-XXXX-" + kd.aadhaar_number.slice(-4) : "—"}
                      </TableCell>
                      <TableCell>
                        {sub.kyc_submitted_at
                          ? new Date(sub.kyc_submitted_at).toLocaleDateString()
                          : "—"}
                      </TableCell>
                      <TableCell>{statusBadge(sub.kyc_status || "pending")}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openReview(sub)}
                          data-testid={`review-kyc-${sub.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" /> Review
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="kyc-review-dialog">
          <DialogHeader>
            <DialogTitle>KYC Review</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Full Name" v={selected.kyc_data?.full_name} />
                <Field label="DOB" v={selected.kyc_data?.date_of_birth} />
                <Field label="Gender" v={selected.kyc_data?.gender} />
                <Field label="Mobile" v={selected.mobile} />
                <Field label="Email" v={selected.email} />
                <Field label="Address" v={selected.kyc_data?.address} />
                <Field label="City / State / PIN" v={`${selected.kyc_data?.city || "-"} / ${selected.kyc_data?.state || "-"} / ${selected.kyc_data?.pin_code || "-"}`} />
              </div>

              <hr />

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>PAN Number</Label>
                  <p className="font-mono">{selected.kyc_data?.pan_number || "—"}</p>
                  <DocumentPreview url={selected.kyc_data?.pan_front_url} label="PAN Image" />
                </div>
                <div>
                  <Label>Aadhaar Number</Label>
                  <p className="font-mono">{selected.kyc_data?.aadhaar_number || "—"}</p>
                  <div className="flex gap-2 mt-2">
                    <DocumentPreview url={selected.kyc_data?.aadhaar_front_url} label="Aadhaar Front" />
                    <DocumentPreview url={selected.kyc_data?.aadhaar_back_url} label="Aadhaar Back" />
                  </div>
                </div>
              </div>

              <DocumentPreview url={selected.kyc_data?.selfie_url} label="Selfie" />

              <hr />

              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Account Holder" v={selected.kyc_data?.account_holder_name} />
                <Field label="Bank Name" v={selected.kyc_data?.bank_name} />
                <Field label="Account Number" v={selected.kyc_data?.account_number} />
                <Field label="IFSC Code" v={selected.kyc_data?.ifsc_code} />
                <Field label="UPI ID" v={selected.kyc_data?.upi_id} />
              </div>

              <hr />

              <div className="space-y-3">
                <div>
                  <Label>Change Status</Label>
                  <Select value={reviewStatus} onValueChange={setReviewStatus}>
                    <SelectTrigger data-testid="kyc-review-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="approved">Approve</SelectItem>
                      <SelectItem value="rejected">Reject</SelectItem>
                      <SelectItem value="under_review">Mark Under Review</SelectItem>
                      <SelectItem value="resubmit">Request Resubmission</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Admin Note (visible to user)</Label>
                  <Textarea
                    value={reviewNote}
                    onChange={(e) => setReviewNote(e.target.value)}
                    placeholder="Reason for rejection or additional notes..."
                    rows={3}
                    data-testid="kyc-review-note"
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>
              Close
            </Button>
            <Button onClick={saveReview} disabled={submitting} data-testid="kyc-review-save">
              {submitting ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

const Field = ({ label, v }) => (
  <div>
    <Label className="text-xs text-gray-500">{label}</Label>
    <p className="text-sm font-medium text-gray-900">{v || "—"}</p>
  </div>
);

const DocumentPreview = ({ url, label }) => {
  if (!url) return (
    <div className="text-xs text-gray-400 border border-dashed rounded-lg p-3 text-center">
      {label}: not uploaded
    </div>
  );
  const isPdf = /\.pdf(\?|$)/i.test(url);
  return (
    <div className="border rounded-lg p-2">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      {isPdf ? (
        <a href={url} target="_blank" rel="noreferrer" className="text-primary-600 text-sm underline">
          Open PDF
        </a>
      ) : (
        <a href={url} target="_blank" rel="noreferrer">
          <img src={url} alt={label} className="max-h-48 rounded" />
        </a>
      )}
    </div>
  );
};

export default AdminKYC;
